import Phaser from 'phaser';
import { Organelle, OrganelleConfig } from './Organelle';
import { SharedResources } from '../utils/SharedResources';

export interface MitochondrionConfig extends OrganelleConfig {
  // Mitochondrion-specific properties
  energyProductionRate: number; // ATP production rate
  size: number; // Size of the mitochondrion
  cristaeCount: number; // Number of cristae (inner membrane folds)
  pulsate?: boolean; // Whether to add pulsation animation
}

export class Mitochondrion extends Organelle {
  private energyProduction: number = 1; // Changed to 1 ATP per second
  private glucoseConsumption: number = 0.1; // 0.1 glucose per second
  private productionActive: boolean = true; // Initial state is active
  private accumulatedTime: number = 0;
  private productionUpdateTimer: number = 0;
  private productionUpdateInterval: number = 1000; // Check every 1000ms
  private resourcesProduced: number = 0; // Track ATP produced
  private atpTextObject: Phaser.GameObjects.Text | null = null; // For +1 ATP visual
  private atpTextTween: Phaser.Tweens.Tween | null = null; // Store the animation tween
  private lastATPVisualTime: number = 0; // Track when the last visual was shown
  
  // Existing properties
  private time: number = 0;
  private mitoSprite!: Phaser.GameObjects.Sprite; // Use definite assignment assertion
  private pulseAnimation: Phaser.Tweens.Tween | null = null;
  private floatOffsetX: number = 0;
  private floatOffsetY: number = 0;
  private originalX: number = 0;
  private originalY: number = 0;
  private floatSpeed: number = 0;
  private floatAngle: number = 0;
  private floatRadius: number = 0;
  private lastCheckedPosition: { x: number, y: number } | null = null;
  private updateTimer: number = 0;
  private skipNextOrientationUpdate: boolean = false; // Flag to skip orientation update
  
  // Static counter to keep track of how many mitochondria were created recently
  // This helps avoid the re-orientation cascade when adding a new mitochondrion
  private static recentlyCreated: boolean = false;
  private static recentCreationTimer: Phaser.Time.TimerEvent | null = null;
  
  // Default configuration for mitochondria
  private static readonly MITOCHONDRION_CONFIG: Partial<MitochondrionConfig> = {
    energyProductionRate: 1, // Updated to 1 ATP per second
    size: 20, // Updated to 1/4 the image size (20px)
    cristaeCount: 8,
    color: 0xFFFFFF, // Set to white (no tint)
    alpha: 1.0, // Fully opaque
    outline: false, // No outline
    outlineColor: 0xFFFFFF,
    movementDelay: 0.95, // Very high value to minimize movement (0.1 -> 0.95)
    wobbleAmount: 0, // No wobble
    maintainPosition: true, // Keep mitochondria firmly in their assigned positions
    detailLevel: 'medium',
    depth: 90, // Between nucleus (80) and membrane (100)
    type: 'mitochondrion',
    pulsate: true // Default to enabling pulsation
  };

  constructor(scene: Phaser.Scene, x: number, y: number, config?: Partial<MitochondrionConfig>) {
    // First create the base config with type
    const baseConfig: Partial<OrganelleConfig> = {
      type: 'mitochondrion'
    };
    
    // Then merge with mitochondrion-specific defaults and provided config
    const fullConfig = {
      ...Mitochondrion.MITOCHONDRION_CONFIG,
      ...config,
      ...baseConfig // Ensure type is not overridden
    };
    
    // Log the initial position for debugging
    console.log(`Creating mitochondrion at position (${x}, ${y})`);
    
    super(scene, x, y, fullConfig);
    
    // When a new mitochondrion is created, set the static flag to prevent others from re-orienting
    if (!Mitochondrion.recentlyCreated) {
      Mitochondrion.recentlyCreated = true;
      
      // Reset the flag after a short delay
      if (Mitochondrion.recentCreationTimer !== null && scene) {
        scene.time.removeEvent(Mitochondrion.recentCreationTimer);
      }
      
      if (scene) {
        Mitochondrion.recentCreationTimer = scene.time.delayedCall(500, () => {
          Mitochondrion.recentlyCreated = false;
          Mitochondrion.recentCreationTimer = null;
        });
      }
    } else {
      // If a mitochondrion was recently created, this one should skip its orientation update
      this.skipNextOrientationUpdate = true;
    }
    
    // Verify position after parent constructor
    console.log(`Mitochondrion position after parent constructor: (${this.x}, ${this.y})`);
    
    // Get SharedResources instance
    const sharedResources = SharedResources.getInstance();
    
    // Check if we need to preload the texture
    if (!sharedResources.hasMitochondrionTexture() && 
        !scene.textures.exists(SharedResources.MITOCHONDRION_TEXTURE_KEY)) {
      console.log('Mitochondrion texture not loaded, preloading now');
      sharedResources.preloadMitochondrionTexture(scene);
      
      // Listen for the complete event to create the sprite after loading
      scene.load.once('complete', () => {
        this.createSprite(scene, fullConfig);
      });
      
      // Start the load if it hasn't started yet
      if (scene.load.isLoading() === false) {
        scene.load.start();
      }
    } else {
      // Texture is already available, create sprite immediately
      this.createSprite(scene, fullConfig);
    }
  }
  
  /**
   * Creates the sprite for the mitochondrion
   * @param scene The scene to add the sprite to
   * @param fullConfig The complete configuration
   */
  private createSprite(scene: Phaser.Scene, fullConfig: Partial<MitochondrionConfig>): void {
    // Create sprite from the shared resource
    const sharedResources = SharedResources.getInstance();
    
    // Use minimal version if in minimal detail mode or specified
    if (fullConfig.detailLevel === 'minimal') {
      // Check if minimal texture needs to be preloaded
      if (!sharedResources.hasMitochondrionMinimalTexture() && 
          !scene.textures.exists(SharedResources.MITOCHONDRION_MIN_TEXTURE_KEY)) {
        console.log('Minimal mitochondrion texture not loaded, preloading now');
        sharedResources.preloadMitochondrionMinimalTexture(scene);
        
        // Listen for the complete event to create the sprite after loading
        scene.load.once('complete', () => {
          // Create the sprite after loading completes
          this.mitoSprite = sharedResources.createMinimalMitochondrionSprite(scene, 0, 0);
          this.finishSpriteSetup(fullConfig);
        });
        
        // Start the load if it hasn't started yet
        if (scene.load.isLoading() === false) {
          scene.load.start();
        }
        return; // Exit early, waiting for texture to load
      }
      
      // Use the minimal outline version (texture already loaded)
      this.mitoSprite = sharedResources.createMinimalMitochondrionSprite(scene, 0, 0);
    } else {
      // Use full detailed version for regular mode
    this.mitoSprite = sharedResources.createMitochondrionSprite(scene, 0, 0);
    }
    
    // Finish setting up the sprite
    this.finishSpriteSetup(fullConfig);
  }
  
  /**
   * Finishes setting up the sprite after creation
   */
  private finishSpriteSetup(fullConfig: Partial<MitochondrionConfig>): void {
    // Set alpha from config
    this.mitoSprite.setAlpha(fullConfig.alpha || 1.0);
    
    // Apply tint if color is specified
    if (fullConfig.color) {
      this.mitoSprite.setTint(fullConfig.color);
    } else {
      this.mitoSprite.clearTint();
    }
    
    // Set blend mode
    this.mitoSprite.setBlendMode(Phaser.BlendModes.NORMAL);
    
    // Add to the container
    this.add(this.mitoSprite);
    
    // Set size
    this.updateSize();
    
    // Position the mitochondrion based on its location relative to the nucleus
    this.orientTowardNucleus();
    
    // Initialize movement
    this.initFloatingMovement();
    
    // Always add subtle pulsation for mitochondria regardless of detail level
    // This ensures the mitochondria don't look completely static
    this.addPulsation();
  }
  
  /**
   * Initialize subtle floating movement parameters
   */
  private initFloatingMovement(): void {
    // Save the original position
    this.originalX = this.x;
    this.originalY = this.y;
    
    // Generate random parameters that will be consistent for each mitochondrion during its lifetime
    const uniqueSeed = Math.random(); // Generate random seed
    
    // Generate movement parameters
    this.floatSpeed = 0.2 + uniqueSeed * 0.3; // Between 0.2 and 0.5
    this.floatAngle = uniqueSeed * Math.PI * 2; // Random starting angle
    this.floatRadius = 1 + uniqueSeed * 1.5; // Small radius of 1-2.5 pixels
  }
  
  /**
   * Add subtle pulsation to the mitochondrion
   */
  private addPulsation(): void {
    if (!this.scene) return;
    
    // Generate a consistent duration using the position as input
    // This makes each mitochondrion's pulsation slightly different
    const positionHash = Math.abs(this.x * 100 + this.y * 100);
    const pulseDuration = 2000 + (positionHash % 1500); // 2-3.5 seconds for slower animation
    
    // Slightly more noticeable scale variation
    const minScale = 0.25 * 0.96; // 4% smaller at minimum (was 2%)
    const maxScale = 0.25 * 1.04; // 4% larger at maximum (was 2%)
    
    // Log that we're adding pulsation
    console.log(`Adding pulsation to mitochondrion at (${this.x.toFixed(2)}, ${this.y.toFixed(2)}) with duration ${pulseDuration}ms`);
    
    // Stop any existing animation
    if (this.pulseAnimation) {
      this.pulseAnimation.stop();
      this.pulseAnimation.remove();
    }
    
    this.pulseAnimation = this.scene.tweens.add({
      targets: this.mitoSprite,
      scaleX: { from: minScale, to: maxScale },
      scaleY: { from: minScale, to: maxScale },
      duration: pulseDuration,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
      delay: (positionHash % 800) // Stagger start time (0-800ms)
    });
  }

  /**
   * Orient the mitochondrion so the curved side faces away from the nucleus
   * For mito_v3_min.png, the curved side faces top-left in the image
   */
  private orientTowardNucleus(): void {
    // Check if we should skip this orientation update
    // This is set when a new mitochondrion is added to prevent all existing ones from re-orienting
    if (this.skipNextOrientationUpdate) {
      // Clear the flag and exit
      this.skipNextOrientationUpdate = false;
      console.log('Skipping orientation update as requested');
      return;
    }
    
    // Calculate angle to the center (nucleus)
    const angleToNucleus = Math.atan2(this.y, this.x);
    
    // We need the OPPOSITE side to face the nucleus
    // So we add PI (180°) to the angle to reverse the direction
    // In mito_v3_min.png, the curved side originally points to approximately -135° (-3π/4)
    // To make the opposite (flat) side face the nucleus, we need to add PI to our original calculation
    const baseRotation = angleToNucleus - Math.PI / 4; // -π/4 instead of +3π/4, effectively rotated 180°
    
    // Add a small random variation (-10° to +10°) for natural appearance, but less than before to reduce jitter
    // We'll use a consistent random factor based on position to prevent jitter during movement
    const positionHash = Math.abs((this.x * 100) + (this.y * 100));
    const randSeed = (positionHash % 100) / 100; // 0-1 value consistently generated from position
    const rotationVariation = (randSeed - 0.5) * Math.PI / 9; // Reduced variation (±10° instead of ±15°)
    
    const finalRotation = baseRotation + rotationVariation;
    
    // Special handling for first-time orientation
    if (!this.lastCheckedPosition) {
      // If this is the first orientation (during initial creation), set rotation directly
      // This prevents any animation which could cause the distracting rotation during addition
      this.mitoSprite.setRotation(finalRotation);
      this.lastCheckedPosition = { x: this.x, y: this.y }; // Initialize position tracking
      return;
    }
    
    // For subsequent orientations, apply the rotation smoothly with a tween
    if (this.mitoSprite && this.scene) {
      // Only rotate if the difference is significant to prevent jitter
      const currentRotation = this.mitoSprite.rotation;
      const rotationDifference = Math.abs(currentRotation - finalRotation);
      
      // Only update rotation if it's different by more than 0.05 radians (~3 degrees)
      if (rotationDifference > 0.05) {
        // Apply rotation with smooth tween
        this.scene.tweens.add({
          targets: this.mitoSprite,
          rotation: finalRotation,
          duration: 600, // Slow, smooth transition
          ease: 'Sine.easeInOut'
        });
      }
    }
    
    // Log the orientation for debugging at reduced frequency
    if (Math.random() < 0.01) { // Only log about 1% of the time
      console.log(`Oriented mitochondrion at (${this.x.toFixed(2)}, ${this.y.toFixed(2)}) with rotation ${(finalRotation * 180 / Math.PI).toFixed(2)}° away from nucleus`);
    }
  }

  /**
   * Update the position and orientation when moved
   */
  setPosition(x: number, y: number, z?: number): this {
    // Check if position would actually change significantly
    const distanceToMove = Math.sqrt(Math.pow(this.x - x, 2) + Math.pow(this.y - y, 2));
    
    // Only update position if it's a significant movement (>2 pixels)
    // This prevents micro-adjustments that cause jitter
    if (distanceToMove > 2) {
      super.setPosition(x, y, z);
      
      // Only re-orient when position changes significantly
      if (this.mitoSprite && this.scene && distanceToMove > 5) {
        this.orientTowardNucleus();
      }
    }
    
    return this;
  }

  /**
   * Override the update method to maintain proper orientation and handle resource production
   */
  update(delta: number, cellX: number, cellY: number, cellVelocity?: Phaser.Math.Vector2, 
         resources?: { glucose: number, atp: number, maxATP: number, isOwnCell?: boolean }): void {
    // Check if we need to enforce minimum distance from nucleus
    this.enforceNucleusDistance(60); // Increased from 45px to 60px for more clearance from nucleus
    
    // Call parent update with reduced frequency to prevent jitter
    // Only update position at most once every 100ms (or whatever the threshold is)
    this.updateTimer = (this.updateTimer || 0) + delta;
    
    // If we've accumulated enough delta time, do the update
    if (this.updateTimer >= 100) { // 100ms threshold
      super.update(delta, cellX, cellY, cellVelocity);
      this.updateTimer = 0; // Reset the timer
      
      // Update orientation less frequently to reduce visual jitter
      if (this.mitoSprite && this.scene) {
        // Only update orientation if position has changed significantly
        const currentX = this.x;
        const currentY = this.y;
        
        if (this.lastCheckedPosition) {
          const dx = currentX - this.lastCheckedPosition.x;
          const dy = currentY - this.lastCheckedPosition.y;
          const distMoved = Math.sqrt(dx*dx + dy*dy);
          
          // Only recalculate orientation if moved more than 5 pixels
          if (distMoved > 5) {
            this.orientTowardNucleus();
            this.lastCheckedPosition = { x: currentX, y: currentY };
          }
        } else {
          this.lastCheckedPosition = { x: currentX, y: currentY };
        }
      }
    }
    
    // Update resource production if resources info is provided
    if (resources) {
      this.updateResourceProduction(delta, resources);
    }
  }
  
  /**
   * Handles ATP production based on available resources
   */
  private updateResourceProduction(delta: number, 
                               resources: { glucose: number, atp: number, maxATP: number, isOwnCell?: boolean }): void {
    const deltaInSeconds = delta / 1000;
    
    // Accumulate time passed
    this.accumulatedTime += deltaInSeconds;
    
    // Check if production conditions are met
    const canProduce = resources.glucose >= this.glucoseConsumption && 
                       resources.atp < resources.maxATP;
    
    // Toggle production state when conditions change
    if (canProduce !== this.productionActive) {
      this.productionActive = canProduce;
      
      // Update visual appearance based on state
      if (this.productionActive) {
        this.startProductionEffect();
      } else {
        this.stopProductionEffect();
      }
    }
    
    // Process production at regular intervals
    this.productionUpdateTimer += delta;
    if (this.productionUpdateTimer >= this.productionUpdateInterval) {
      this.productionUpdateTimer = 0;
      
      if (this.productionActive) {
        // Calculate resources produced/consumed
        const atpProduced = Math.floor(this.energyProduction);
        const glucoseConsumed = this.glucoseConsumption;
        
        // Store production amount for use by Cell
        this.resourcesProduced = atpProduced;
        
        // Show ATP production visual effect for own cell, but not too frequently
        if (resources.isOwnCell !== false) {
          // Check if enough time has passed since last visual (at least 1.5 seconds)
          const currentTime = this.scene ? this.scene.time.now : 0;
          const timeSinceLastVisual = currentTime - this.lastATPVisualTime;
          
          // Only show visual if no current animation is playing or it's been a while
          if (!this.atpTextObject || !this.atpTextTween || !this.atpTextTween.isPlaying() || 
              timeSinceLastVisual > 1500) {
            this.showATPProductionEffect();
          }
        }
      }
    }
  }

  /**
   * Shows a visual +1 effect when ATP is produced
   */
  private showATPProductionEffect(): void {
    if (!this.scene) return;
    
    // If there's already an animation in progress, don't interrupt it
    if (this.atpTextObject && this.atpTextObject.active) {
      return;
    }
    
    // Create a floating "+1" text that rises and fades out
    if (this.atpTextObject) {
      this.atpTextObject.destroy();
      this.atpTextObject = null;
    }
    
    // Create a new text object with improved visibility
    this.atpTextObject = this.scene.add.text(0, -20, "+1", {
      fontSize: '16px',          // Slightly larger text
      fontFamily: 'Arial',
      color: '#00FF00',          // Bright green
      stroke: '#000000',
      strokeThickness: 3,        // Thicker outline for better visibility
      shadow: {                  // Add shadow for better contrast
        offsetX: 1,
        offsetY: 1,
        color: '#000000',
        blur: 2,
        stroke: true,
        fill: true
      }
    });
    
    // Center the text and add to the container
    this.atpTextObject.setOrigin(0.5, 0.5);
    
    // Set a higher depth to ensure it's visible above other elements
    this.atpTextObject.setDepth(200);
    
    this.add(this.atpTextObject);
    
    // Store the current time to prevent rapid recreation
    this.lastATPVisualTime = this.scene.time.now;
    
    // Animate the text rising and fading out with a longer duration
    const animTween = this.scene.tweens.add({
      targets: this.atpTextObject,
      y: -60,                   // Move up higher for more visibility
      alpha: 0,                 // Fade out
      duration: 2000,           // Longer duration (2 seconds instead of 1)
      ease: 'Power1',
      onComplete: () => {
        if (this.atpTextObject) {
          this.atpTextObject.destroy();
          this.atpTextObject = null;
        }
      }
    });
    
    // Store the tween so we can check if it's still active
    this.atpTextTween = animTween;
  }
  
  /**
   * Activates the ATP production visual effect
   */
  private startProductionEffect(): void {
    // Clear tint to show active state
    if (this.mitoSprite) {
      this.mitoSprite.clearTint();
      
      // Increase pulsation animation speed for active state
      if (this.pulseAnimation) {
        this.pulseAnimation.timeScale = 1.5;
      }
    }
  }

  /**
   * Deactivates the ATP production visual effect
   */
  private stopProductionEffect(): void {
    // Add dim tint to show inactive state
    if (this.mitoSprite) {
      this.mitoSprite.setTint(0xAAAAAA);
      
      // Slow down pulsation for inactive state
      if (this.pulseAnimation) {
        this.pulseAnimation.timeScale = 0.5;
      }
    }
    
    // Clean up any existing ATP text animation
    if (this.atpTextTween && this.atpTextTween.isPlaying()) {
      this.atpTextTween.stop();
      this.atpTextTween = null;
    }
    
    if (this.atpTextObject) {
      this.atpTextObject.destroy();
      this.atpTextObject = null;
    }
  }

  /**
   * Returns the current production rates
   */
  public getProductionRates(): { atp: number, glucose: number } {
    return {
      atp: this.energyProduction,
      glucose: this.glucoseConsumption
    };
  }

  /**
   * Returns whether the mitochondrion is currently producing ATP
   */
  public isProducing(): boolean {
    return this.productionActive;
  }
  
  /**
   * Updates the mitochondrion with current cell resource information
   * This allows the mitochondrion to know the cell's current resource state
   * without waiting for the next update cycle
   */
  public updateResourceInfo(resources: { cellGlucose: number, cellATP: number, cellMaxATP: number }): void {
    // Check if production conditions are met
    const canProduce = resources.cellGlucose >= this.glucoseConsumption && 
                       resources.cellATP < resources.cellMaxATP;
    
    // Toggle production state when conditions change
    if (canProduce !== this.productionActive) {
      this.productionActive = canProduce;
      
      // Update visual appearance based on state
      if (this.productionActive) {
        this.startProductionEffect();
      } else {
        this.stopProductionEffect();
      }
    }
  }
  
  /**
   * Returns and resets the resources produced by this mitochondrion
   */
  public getAndResetResourcesProduced(): number {
    const produced = this.resourcesProduced;
    this.resourcesProduced = 0;
    return produced;
  }
  
  /**
   * Ensures the mitochondrion maintains a minimum safe distance from the nucleus
   * @param minDistance Minimum distance to keep from the nucleus center
   */
  private enforceNucleusDistance(minDistance: number): void {
    // Calculate current distance from center (nucleus)
    const distanceFromCenter = Math.sqrt(this.x * this.x + this.y * this.y);
    
    // If we're too close to the nucleus
    if (distanceFromCenter < minDistance) {
      // Calculate the angle from center
      const angle = Math.atan2(this.y, this.x);
      
      // Calculate the position at the safe distance
      const safeX = Math.cos(angle) * minDistance;
      const safeY = Math.sin(angle) * minDistance;
      
      // Move smoothly to the safe position with tween
      if (this.scene) {
        this.scene.tweens.add({
          targets: this,
          x: safeX,
          y: safeY,
          duration: 800, // Slow, smooth transition
          ease: 'Sine.easeOut'
        });
        
        // If we have position tracking properties, update those too
        if (this.initialPosition) {
          this.initialPosition.x = safeX;
          this.initialPosition.y = safeY;
        }
        
        if (this.targetPosition) {
          this.targetPosition.set(safeX, safeY);
        }
      } else {
        // Fallback if no scene (should never happen)
        super.setPosition(safeX, safeY);
      }
      
      // Log the repositioning
      console.log(`Enforced nucleus distance: moved mitochondrion from ${distanceFromCenter.toFixed(1)}px to ${minDistance}px from center`);
    }
  }

  // Override the parent draw method to prevent drawing the background circle
  protected draw(): void {
    // Clear any previous graphics
    this.mainShape.clear();
    
    // We're not drawing a fallback shape here because we're using the sprite instead
    // This ensures no background circle is drawn
  }

  /**
   * Moves the mitochondrion away from the nucleus (center point) to a safe distance
   * @param minSafeDistance The minimum safe distance from the center
   */
  private moveAwayFromNucleus(minSafeDistance: number): void {
    // Calculate current distance and angle from center
    const currentDistanceFromCenter = Math.sqrt(this.x * this.x + this.y * this.y);
    let angle = Math.atan2(this.y, this.x);
    
    // If exactly at center (very unlikely), pick a random angle
    if (currentDistanceFromCenter < 0.1) {
      angle = Math.random() * Math.PI * 2;
    }
    
    // Skip if already at safe distance to prevent unnecessary movements
    if (currentDistanceFromCenter >= minSafeDistance) {
      return;
    }
    
    // Calculate the target position at the safe distance
    const targetX = Math.cos(angle) * minSafeDistance;
    const targetY = Math.sin(angle) * minSafeDistance;
    
    // Calculate how far inside the safe zone we are
    const distanceInsideSafeZone = minSafeDistance - currentDistanceFromCenter;
    
    // Calculate a smooth movement factor - stronger the deeper we are in the safe zone
    // Use a sigmoid-like curve for smoother acceleration
    const smoothFactor = Math.min(0.3, distanceInsideSafeZone * 0.01);
    
    // Apply smooth movement - move a percentage of the way toward target
    const newX = this.x + (targetX - this.x) * smoothFactor;
    const newY = this.y + (targetY - this.y) * smoothFactor;
    
    // Update position
    this.setPosition(newX, newY);
    
    // Also smoothly update the initialPosition to gradually stabilize at safe distance
    if (this.initialPosition) {
      this.initialPosition.x += (targetX - this.initialPosition.x) * (smoothFactor * 0.5);
      this.initialPosition.y += (targetY - this.initialPosition.y) * (smoothFactor * 0.5);
    }
    
    // Also update the targetPosition if available
    if (this.targetPosition) {
      this.targetPosition.set(this.initialPosition.x, this.initialPosition.y);
    }
    
    // No need to reset floating parameters, just continue with current values
    // This prevents jerky resets in the fluid motion
  }

  destroy(fromScene?: boolean): void {
    // Stop and destroy any animations
    if (this.pulseAnimation) {
      this.pulseAnimation.stop();
      this.pulseAnimation.remove();
      this.pulseAnimation = null;
    }
    
    // Stop ATP text animation
    if (this.atpTextTween) {
      this.atpTextTween.stop();
      this.atpTextTween = null;
    }
    
    // Destroy ATP text if it exists
    if (this.atpTextObject) {
      this.atpTextObject.destroy();
      this.atpTextObject = null;
    }
    
    // Call parent destroy method
    super.destroy(fromScene);
  }

  private updateSize(): void {
    // Scale to 1/4 the natural size (20px instead of 80px)
    this.mitoSprite.setScale(0.25);
    
    // Set the size property in case it's needed elsewhere
    const fullConfig = this.config as MitochondrionConfig;
    const size = fullConfig.size || 20; // Update default size to 20
    
    console.log(`Scaled mitochondrion to 20px width (25% of natural size)`);
  }

  getEnergyProduction(): number {
    return this.energyProduction;
  }

  setEnergyProduction(amount: number): void {
    this.energyProduction = Math.max(0, amount);
  }
  
  /**
   * Returns the ATP capacity bonus provided by this mitochondrion
   */
  public getATPCapacityBonus(): number {
    return 5; // Each mitochondrion adds +5 ATP capacity
  }
} 